/**
 * Quarantine review queue — detail / decision page.
 *
 * Routed at /quarantine/:itemId. Loads the item via
 * apiv1.quarantine.getItem(), renders its scan verdict + a download link
 * for admin inspection, and Release / Delete controls.
 *
 * Permanent delete requires typing the exact filename before the button
 * enables (found missing by PR review) — the same typed-confirmation shape
 * this app already established for its other irreversible actions
 * (MDM's "Unmerge Cluster" dialog, PurgeProjectDataDialog). This delete
 * hard-removes the object-storage blob with no undo — a lighter guard than
 * the app's own bar for destructive actions was a real gap, not a style
 * choice.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { quarantine, type QuarantineItemResponse } from "../../../apiv1";

interface Props {
  onBack?: () => void;
}

type DecisionState = "idle" | "submitting";

export function QuarantineDetail({ onBack }: Props) {
  const navigate = useNavigate();
  const params = useParams<{ itemId: string }>();
  const itemId = params.itemId;

  const [item, setItem] = useState<QuarantineItemResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [decisionState, setDecisionState] = useState<DecisionState>("idle");
  const [notes, setNotes] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const fetched = await quarantine.getItem(itemId);
        if (!cancelled) setItem(fetched);
      } catch (e) {
        if (!cancelled)
          setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  if (!itemId) {
    return <div className="p-6 text-white/40 text-sm">No item id in URL.</div>;
  }
  if (loading) {
    return <div className="p-6 text-white/40 text-sm">Loading…</div>;
  }
  if (loadError) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded">
          {loadError}
        </div>
        <button
          onClick={() => (onBack ? onBack() : navigate("/quarantine"))}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/15 text-sm rounded"
        >
          Back to quarantine
        </button>
      </div>
    );
  }
  if (!item) {
    return <div className="p-6 text-white/40 text-sm">Item not found.</div>;
  }

  const isTerminal = item.status !== "quarantined";
  const isSecondary = item.detection_stage === "secondary";
  const deleteConfirmed = deleteConfirmText.trim() === item.original_filename;

  async function submit(action: "release" | "delete") {
    if (!itemId) return;
    if (action === "delete" && !notes.trim()) {
      setDecisionError(
        "Notes are required when permanently deleting a quarantined file.",
      );
      return;
    }
    if (action === "delete" && !deleteConfirmed) {
      setDecisionError(
        "Type the exact filename to confirm permanent deletion.",
      );
      return;
    }
    setDecisionError(null);
    setDecisionState("submitting");
    try {
      const updated =
        action === "release"
          ? await quarantine.releaseItem(itemId, {
              notes: notes.trim() || null,
            })
          : await quarantine.deleteItem(itemId, {
              notes: notes.trim() || null,
            });
      setItem(updated);
      setDecisionState("idle");
    } catch (e) {
      setDecisionError(e instanceof Error ? e.message : String(e));
      setDecisionState("idle");
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => (onBack ? onBack() : navigate("/quarantine"))}
          className="text-xs text-white/40 hover:text-white/70"
        >
          ← Back to quarantine
        </button>
        <span className="text-xs font-mono text-white/30">{item.id}</span>
      </div>

      <h1 className="text-2xl font-semibold mb-1 break-all">
        {item.original_filename}
      </h1>
      <p className="text-sm text-white/60 mb-6">
        Uploaded via <span className="font-mono">{item.source_module}</span>
        {item.uploaded_by && (
          <>
            {" "}
            by <span className="font-mono">{item.uploaded_by}</span>
          </>
        )}
      </p>

      {isSecondary && (
        <div className="mb-6 p-3 bg-orange-500/10 border border-orange-500/30 text-orange-200 text-sm rounded">
          Flagged by the <strong>secondary</strong> (cloud-native) scanner — the
          primary ClamAV gate already cleared this file and it had already
          landed in normal storage when the secondary scan's verdict arrived.
          Two independent engines disagreeing is worth extra scrutiny before
          releasing. See docs/malware-scanning-design.md.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-white/5 border border-white/10 rounded">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            Detection stage
          </div>
          <div className="text-sm capitalize">
            {isSecondary ? (
              <span className="text-orange-300">secondary</span>
            ) : (
              <span className="text-white/70">primary</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            Detected signature
          </div>
          <div className="text-sm font-mono text-red-300">
            {item.detected_signature}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            Size
          </div>
          <div className="text-sm font-mono">
            {(item.size_bytes / 1024).toFixed(1)} KB
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            Status
          </div>
          <div className="text-sm capitalize">{item.status}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-white/40">
            Uploaded
          </div>
          <div className="text-sm">
            {new Date(item.created_at).toLocaleString()}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <a
          href={quarantine.downloadUrl(item.id)}
          className="inline-block px-3 py-1.5 text-xs bg-white/10 hover:bg-white/15 rounded"
        >
          Download for inspection ↓
        </a>
        <span className="ml-2 text-[11px] text-white/40">
          Requires quarantine:review — the bytes are still the file that failed
          the scan.
        </span>
      </div>

      {!isTerminal ? (
        <div className="p-4 bg-white/5 border border-white/10 rounded">
          {decisionError && (
            <div
              className="mb-3 p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded"
              role="alert"
              data-testid="decision-error"
            >
              {decisionError}
            </div>
          )}
          <label className="block text-[11px] uppercase tracking-wider text-white/40 mb-2">
            Notes (required on permanent delete)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Capture your reasoning so the audit trail is complete."
            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-sm font-mono"
          />
          <label className="block text-[11px] uppercase tracking-wider text-white/40 mt-3 mb-2">
            Type the exact filename to enable permanent delete
          </label>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={item.original_filename}
            autoComplete="off"
            spellCheck={false}
            className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded text-sm font-mono"
            data-testid="delete-confirm-input"
          />
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => submit("delete")}
              disabled={decisionState === "submitting" || !deleteConfirmed}
              className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 text-sm rounded disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="delete-button"
              title={
                deleteConfirmed
                  ? undefined
                  : "Type the exact filename above to enable"
              }
            >
              Permanently delete
            </button>
            <button
              onClick={() => submit("release")}
              disabled={decisionState === "submitting"}
              className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 text-sm rounded disabled:opacity-50"
              data-testid="release-button"
            >
              Release
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-white/5 border border-white/10 rounded text-sm">
          <div className="text-white/60">
            Decided{" "}
            {item.decision_at
              ? new Date(item.decision_at).toLocaleString()
              : ""}{" "}
            by{" "}
            <span className="font-mono text-xs">
              {item.reviewer_id ?? "system"}
            </span>
          </div>
          {item.notes && (
            <div className="mt-2 text-xs text-white/50">{item.notes}</div>
          )}
        </div>
      )}
    </div>
  );
}
